import React, { useEffect, useState } from "react";
import { useWorkerCreation } from "@/context/WorkerCreationContext";
import { WorkerAniAPI } from "@/services/api";
import type { WorkerTemplate } from "@/services/api/worker-ani/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserAstronaut } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import WorkerAvatar from "@/components/worker/WorkerAvatar";

interface CreateWorkerTemplateSelectProps {
  selectedTemplate: WorkerTemplate | null;
  onSelect: (template: WorkerTemplate | null) => void;
  onNext: () => void;
}

const customWorkerTemplate: WorkerTemplate = {
  name: "Custom Worker",
  description: "Start from scratch",
  default_job_title: "Custom AI Worker",
  default_bio: "",
  default_rules: [],
  created_at: "",
  updated_at: "",
};

export default function CreateWorkerTemplateSelect({
  selectedTemplate,
  onSelect,
}: CreateWorkerTemplateSelectProps) {
  const [templates, setTemplates] = useState<WorkerTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const data = await WorkerAniAPI.getWorkerTemplates();
        setTemplates(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load templates"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  return (
    <div>
      <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-2">
        Choose a Template
      </h2>
      <p className="text-slate-500 dark:text-slate-400 mb-6">
        Start with a pre-built template or create a custom worker from scratch.
      </p>
      {loading && (
        <div className="py-8 text-center text-slate-500">
          Loading templates...
        </div>
      )}
      {error && <div className="py-8 text-center text-red-500">{error}</div>}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {templates.map((template) => (
            <div
              key={template.name}
              className={`p-4 rounded-2xl border-2 cursor-pointer transition-colors flex items-center gap-3 ${
                selectedTemplate?.name === template.name
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                  : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
              }`}
              onClick={() => onSelect(template)}
            >
              {template.avatar ? (
                <WorkerAvatar seed={template.name} size={40} />
              ) : (
                <div className="bg-indigo-100 dark:bg-indigo-200/10 p-2 size-10 flex items-center justify-center rounded-xl">
                  <FontAwesomeIcon
                    icon={faUserAstronaut}
                    className="text-xl text-indigo-600 dark:text-white/50"
                  />
                </div>
              )}
              <div>
                <h4 className="font-semibold text-slate-800 dark:text-slate-200">
                  {template.name}
                </h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {template.default_job_title}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  {template.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
